/**
 * Catalog entries for the record pattern: one list and one page for every
 * record people manage (build contract §1E). An item, a commitment and the
 * next record are built from these, never beside them.
 *
 *   EntityListPage   the list: tiles, search, filters, sort, group, columns, a peek per row
 *   ListTiles        the counts across the top, each a filter
 *   DetailPageFrame  a record's own page: back link, chips, title, standing band, tabs, rail
 *   MasterDetailShell the Inbox view: a queue worked one at a time, the record beside it
 */
import { FrameDemo, InboxDemo, ListDemo, TilesDemo } from './componentCatalog.recordsHarness';
import type { CatalogEntry } from './componentCatalog.types';

export const RECORD_CATALOG: CatalogEntry[] = [
  {
    id: 'record-list-tiles',
    name: 'ListTiles',
    tier: 'molecule',
    status: 'live',
    importPath: '@/components/collection/ListTiles',
    description:
      'The counts across the top of a list. A tile with a filter narrows the list and lets go on a second press; a tile without one reads only. On a list, set them through EntityListPage `tiles`, not by hand.',
    render: () => <TilesDemo />,
    props: [
      { name: 'tiles', type: 'ListTile[]', required: true, description: 'id, label, value, tone, hint; filters: false for a tile that only reads.' },
      { name: 'activeId', type: 'string | null', description: 'The tile pressed now.' },
      { name: 'onSelect', type: '(id | null) => void', description: 'Without it the tiles are readings, not buttons.' },
    ],
  },
  {
    id: 'record-list',
    name: 'EntityListPage (tiles · peek)',
    tier: 'template',
    status: 'live',
    importPath: '@/components/collection/EntityListPage',
    description:
      'The one list for records people manage: header, tiles, search, filters, sort, group, columns, cards on a phone, and a peek under each table row with a link to the record’s page. Commitments, Strategies and the planning lists run on it.',
    render: () => <ListDemo />,
    props: [
      { name: 'tiles', type: 'EntityListTile<T>[]', description: 'Counts across the top. `match` makes a tile a filter; tone may be a function of the count. Hidden while the list is empty.' },
      { name: 'activeTileId / onActiveTileChange', type: 'string | null', description: 'Keep the pressed tile in the URL so Back to the list returns to it.' },
      { name: 'renderPeek', type: '(row) => ReactNode', description: 'A chevron opens a few facts under the row and a link to its page; one row at a time.' },
      { name: 'renderCard', type: '(row) => ReactNode', description: 'With viewModes including card, a phone opens the list as cards.' },
    ],
    usage: `<EntityListPage
  items={rows} getId={(r) => r.id} title="Commitment Registry" …
  tiles={[{ id: 'active', label: 'Active', match: (r) => r.status === 'active' }]}
  renderPeek={(r) => <Peek row={r} />}
  onItemClick={(r) => navigate(\`/…/\${r.id}\`)}
/>`,
  },
  {
    id: 'record-detail-frame',
    name: 'DetailPageFrame · DetailRail',
    tier: 'template',
    status: 'live',
    importPath: '@/components/detail-page/DetailPageFrame',
    description:
      'A record’s own page, the item page’s frame: back link to the list as it was left, identity chips and actions, title, the standing band, tabs, and the At a glance rail whose rows edit in place. The item page and the commitment page are both drawn in it.',
    render: () => <FrameDemo />,
    props: [
      { name: 'back', type: '{ label, href? }', required: true, description: 'Pass the list URL with its search so filters come back.' },
      { name: 'identity / actions', type: 'ReactNode', description: 'Left and right of the header row.' },
      { name: 'title / description / headline', type: 'ReactNode', description: 'headline replaces both, for a page with its own title editor.' },
      { name: 'standing', type: '{ label, value, note? }[]', description: 'Where the record stands, three or four cells.' },
      { name: 'tabs / activeTab / onTabChange', type: 'DetailTab[]', required: true, description: 'Keep the tab in the URL (?tab=).' },
      { name: 'rail', type: 'ReactNode', description: 'Usually <DetailRail groups={…} />.' },
    ],
  },
  {
    id: 'record-inbox',
    name: 'Inbox view (MasterDetailShell)',
    tier: 'template',
    status: 'live',
    importPath: '@/components/master-detail/MasterDetailShell',
    description:
      'The split view: a narrow list with the chosen record beside it. Only for a queue a person works through one at a time (Tasks, Notifications, asks, intake triage). Never for a registry people search, sort or bulk-edit, and never for a container whose detail is itself a list (a pool): those use the list and the page above. It holds a few and fails at many (§1E: zero, one, few, many).',
    render: () => <InboxDemo />,
    props: [
      { name: 'master / detail', type: 'ReactNode', required: true, description: 'The queue, and the record chosen from it.' },
      { name: 'selectedId / onSelectionChange', type: 'string | null', description: 'Controlled selection; Escape closes the detail.' },
      { name: 'syncToUrl / urlParamName', type: 'boolean / string', default: "true / 'selected'", description: 'Keep the chosen record in the URL so a link opens it.' },
      { name: 'showDetailAsModal', type: 'boolean', default: 'true', description: 'On a phone the record opens over the queue.' },
      { name: 'defaultMasterWidth / widthStorageKey', type: 'number / string', description: 'The queue is resizable; its width is remembered per key.' },
    ],
  },
];
